import { gsap } from 'gsap';
import isHidden from '../../functions/isHidden';

const Tabs = (el) => {
    let panelIsOpen = null;
    const alwaysOpen = el.classList.contains('interactive-image-tabs--always-open');
    const panels = el.querySelectorAll('.interactive-image-tabs__content-item');
    const buttons = [...panels].reduce((acc, panel) => {
        const btnGroup = Array.prototype.slice.call(document.querySelectorAll(`[aria-controls="${panel.id}"]`));
        return [
            ...acc,
            ...btnGroup
        ];
    }, []);
    const navigationDiv = el.querySelector('.interactive-image-tabs__navigation');
    const tabButtons = navigationDiv
        ? [...navigationDiv.querySelectorAll('[role="tab"]')]
        : [...buttons].filter(btn => btn.getAttribute('role') === 'tab');

    const open = (activeId) => {
        const activePanel = document.getElementById(activeId);
        const activeButtons = [...buttons].filter((button) => button.getAttribute('aria-controls') === activeId);
        let visibleActiveButton = null;

        for (let button of activeButtons) {
            button.setAttribute('aria-expanded', true);

            if (!isHidden(button) && button.getAttribute('role') === 'tab') {
                visibleActiveButton = button;
            }
        }

        activePanel.setAttribute('aria-hidden', false);
        el.classList.add('interactive-image-tabs--open');
        panelIsOpen = activePanel;

        updateTabIndexes(activeId);

        // Scroll to button if panel is not sufficiently visible
        const rect = activePanel.getBoundingClientRect();
        const windowHeight = window.innerHeight || document.documentElement.clientHeight;
        const visibleHeight = Math.max(0, Math.min(rect.bottom, windowHeight) - Math.max(rect.top, 0));
        const visibleRatio = visibleHeight / rect.height;
        const header = document.querySelector('.header');
        const offsetY = header ? header.offsetHeight : 0;
        if (visibleRatio < 0.1 && visibleActiveButton) {
            gsap.to(window, { duration: 0.3, scrollTo: { y: visibleActiveButton, offsetY } });
        }
    };

    const close = () => {
        panelIsOpen = null;
        el.classList.remove('interactive-image-tabs--open');
    };

    const toggle = (activeId) => {
        resetTabs();

        if (!panelIsOpen) {
            open(activeId);
        } else {
            if (panelIsOpen.getAttribute('id') === activeId) {
                // If alwaysOpen is true, don't close the panel
                if (!alwaysOpen) {
                    close();
                } else {
                    open(activeId);
                }
            } else {
                open(activeId);
            }
        }
    };

    const resetTabs = () => {
        for (let panel of panels) {
            panel.setAttribute('aria-hidden', true);
        }

        for (let button of buttons) {
            button.setAttribute('aria-expanded', false);
        }
    };

    const updateTabIndexes = (activeId) => {
        for (let button of tabButtons) {
            const isActive = button.getAttribute('aria-controls') === activeId;
            button.setAttribute('tabindex', isActive ? '0' : '-1');
        }
    };

    const handleKeydown = (e) => {
        if (!['ArrowLeft', 'ArrowRight'].includes(e.key)) return;
        e.preventDefault();
        const currentIndex = tabButtons.indexOf(e.currentTarget);
        const delta = e.key === 'ArrowRight' ? 1 : -1;
        const nextIndex = (currentIndex + delta + tabButtons.length) % tabButtons.length;
        const nextButton = tabButtons[nextIndex];
        toggle(nextButton.getAttribute('aria-controls'));
        nextButton.focus();
    };

    const addEventListeners = () => {
        for (let button of buttons) {
            button.addEventListener('click', (e) => {
                const id = e.currentTarget.getAttribute('aria-controls');
                toggle(id);
            });
        }

        for (let button of tabButtons) {
            button.addEventListener('keydown', handleKeydown);
        }
    };

    const init = () => {
        // Initialize tabindex: first button is 0, rest are -1
        tabButtons.forEach((button, index) => {
            button.setAttribute('tabindex', index === 0 ? '0' : '-1');
        });

        addEventListeners();

        // If alwaysOpen is true, open the first tab by default
        if (alwaysOpen && panels.length > 0) {
            open(panels[0].id);
        }
    };

    init();
};

export default Tabs;
